// src/components/SecureFileViewer.jsx
import { useEffect, useMemo, useState, useRef } from 'react';
import JSZip from 'jszip';
import PropTypes from 'prop-types';
import { fetchBlob, downloadBlob, guessContentType } from '../utils/secureFiles';

// Détecte le type par "signature magique" sur quelques formats courants
async function sniffContentType(blob) {
  try {
    const head = blob.slice(0, 16);
    const buf = new Uint8Array(await head.arrayBuffer());
    const str = String.fromCharCode(...buf);
    // PDF
    if (str.startsWith('%PDF-')) return 'application/pdf';
    // PNG
    if (buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4E && buf[3] === 0x47) return 'image/png';
    // JPG
    if (buf[0] === 0xFF && buf[1] === 0xD8 && buf[2] === 0xFF) return 'image/jpeg';
    // WEBP : "RIFF" .... "WEBP"
    if (str.startsWith('RIFF') && str.slice(8, 12) === 'WEBP') return 'image/webp';
    // GIF
    if (str.startsWith('GIF87a') || str.startsWith('GIF89a')) return 'image/gif';
    // MP4 (ftyp)
    if (buf[4] === 0x66 && buf[5] === 0x74 && buf[6] === 0x79 && buf[7] === 0x70) return 'video/mp4';
    // WEBM
    if (buf[0] === 0x1A && buf[1] === 0x45 && buf[2] === 0xDF && buf[3] === 0xA3) return 'video/webm';
    // OGG
    if (str.startsWith('OggS')) return 'video/ogg';
    // WAV
    if (str.startsWith('RIFF') && str.slice(8, 12) === 'WAVE') return 'audio/wav';
    // MP3
    if (str.startsWith('ID3') || (buf[0] === 0xFF && (buf[1] & 0xE0) === 0xE0)) return 'audio/mpeg';
  } catch {}
  return null;
}

function toObjectUrl(blob) {
  const url = URL.createObjectURL(blob);
  return {
    url,
    revoke: () => URL.revokeObjectURL(url),
  };
}

export default function SecureFileViewer({ source, onError, height = 600, showDownload = true, filename }) {
  const [items, setItems] = useState([]); // [{name, contentType, blob, objectUrl}]
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const cleanupRef = useRef([]);

  const tabs = items;

  const isArraySource = Array.isArray(source);

  useEffect(() => {
    let cancelled = false;

    // Corrige le type si le serveur renvoie application/octet-stream (ou rien)
    async function fixBlobType(name, blob, serverType) {
      let ct = serverType || '';
      if (!ct || ct === 'application/octet-stream') {
        // 1) essaie via l’extension
        const byName = guessContentType(name || '');
        // 2) essaie via la signature magique
        const byMagic = await sniffContentType(blob);
        ct = byMagic || byName || 'application/octet-stream';
      }
      // Remet le bon type dans le Blob si nécessaire
      if (blob.type !== ct) {
        const ab = await blob.arrayBuffer();
        blob = new Blob([ab], { type: ct });
      }
      return { blob, contentType: ct };
    }

    async function loadOne(url, nameHint) {
      const { blob, contentType } = await fetchBlob(url);
      const name = nameHint || (url.split('/').pop() || 'document');
      const fixed = await fixBlobType(name, blob, contentType);
      return { name, ...fixed };
    }

    async function loadZip(zipBlob) {
      const zip = await JSZip.loadAsync(zipBlob);
      const files = [];
      for (const [filename, entry] of Object.entries(zip.files)) {
        if (entry.dir || filename.startsWith('__')) continue;
        const data = await entry.async('uint8array');
        const ct = guessContentType(filename);
        files.push({ name: filename, blob: new Blob([data], { type: ct }), contentType: ct });
      }
      if (!files.length) throw new Error('Aucun fichier compatible trouvé dans le ZIP.');
      return files;
    }

    async function run() {
      setLoading(true);
      setItems([]);
      cleanupRef.current.forEach(fn => fn?.());
      cleanupRef.current = [];
      try {
        const collected = [];

        if (isArraySource) {
          for (const item of source) {
            const one = await loadOne(item.url, item.name);
            collected.push(one);
          }
        } else if (typeof source === 'string' && source) {
          const { blob, contentType } = await fetchBlob(source);
          const name = filename || (source.split('/').pop() || 'document');
          const fixed = await fixBlobType(name, blob, contentType);
          const isZip = fixed.contentType?.startsWith('application/zip');
          if (isZip) {
            const list = await loadZip(fixed.blob);
            collected.push(...list);
          } else {
            collected.push({ name, ...fixed });
          }
        }

        const withUrls = collected.map(f => {
          const ou = toObjectUrl(f.blob);
          cleanupRef.current.push(ou.revoke);
          return { ...f, objectUrl: ou.url };
        });

        if (!cancelled) {
          setItems(withUrls);
          setActive(0);
        }
      } catch (e) {
        if (!cancelled) {
          onError?.(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
      cleanupRef.current.forEach(fn => fn?.());
      cleanupRef.current = [];
    };
  }, [source, isArraySource, filename, onError]);

  const current = items[active];

  const Viewer = useMemo(() => {
    if (!current) return null;
    // Si on n’a toujours pas de type correct, tente PDF si le nom finit par .pdf
    const ct = current.contentType || (current.name?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream');
    const style = { width: '100%', height: `${height}px`, minHeight: `${height}px` };

    if (ct.startsWith('image/')) {
      return <img src={current.objectUrl} alt={current.name} style={{ ...style, objectFit: 'contain', display: 'block' }} />;
    }
    if (ct.startsWith('video/')) {
      return <video src={current.objectUrl} controls style={style} />;
    }
    if (ct === 'application/pdf' || ct.startsWith('text/')) {
      return <embed src={current.objectUrl} type={ct} style={style} />;
    }
    return (
      <div style={{ padding: 16 }}>
        <p>Type non prévisualisable (<code>{ct}</code>). Téléchargez le fichier ci-dessous.</p>
      </div>
    );
  }, [current, height]);

  if (loading) return <div style={{ padding: 16 }}>Chargement…</div>;
  if (!items.length) return <div style={{ padding: 16 }}>Aucun fichier à afficher.</div>;

  return (
    <div className="secure-file-viewer">
      {items.length > 1 && (
        <div className="nav nav-tabs mb-2" role="tablist" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
          {tabs.map((t, i) => (
            <button
              key={i}
              className={`nav-link ${i === active ? 'active' : ''}`}
              onClick={() => setActive(i)}
              type="button"
            >
              {t.name}
            </button>
          ))}
        </div>
      )}

      {Viewer}

      {showDownload && current && (
        <div className="mt-2" style={{ textAlign: 'right' }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => downloadBlob(current.blob, current.name)}>
            Télécharger
          </button>
        </div>
      )}
    </div>
  );
}

SecureFileViewer.propTypes = {
  source: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, url: PropTypes.string.isRequired })),
  ]).isRequired,
  onError: PropTypes.func,
  height: PropTypes.number,
  showDownload: PropTypes.bool,
  filename: PropTypes.string,
};
